import { Redirect, Switch, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { isAuthed, isGuest } from 'redux/query';
import { Spinner } from 'Common';
import { linkHandler } from 'utils/linkHandler';
import { Suspense, useEffect, useMemo } from 'react';
import { Route } from 'react-router-dom';
import NotFoundPage from 'components/NotFoundPage';
import { useRouteData } from 'Application/RouteDataProvider/RouteDataProvider';
import { REDIRECTS } from 'config/redirects';

const CustomContentRenderer = ({ Component, routeKey, ...props }) => {
  const { currentRoute, setCurrentRoute } = useRouteData();
  const params = useParams();

  useEffect(() => {
    if (currentRoute?.key !== routeKey || currentRoute?.path !== props.path) {
      setCurrentRoute({ ...props, key: routeKey, params });
    }
  }, []);

  if (currentRoute?.redirectTo) {
    return <Redirect to={currentRoute.redirectTo} />;
  }

  return <Component {...props} routeKey={routeKey} />;
};

export const Router = ({ children }) => {
  const authed = useSelector(isAuthed);
  const guest = useSelector(isGuest);

  const renderFallback = () => {
    return <Spinner />;
  };

  const routes = useMemo(() => {
    const _routes = linkHandler.getAll();
    return Object.keys(_routes).map((routeKey) => _routes[routeKey]);
  }, []);

  const redirects = useMemo(() => routes.filter((route) => !!route.redirects?.length), [routes]);

  return (
    <Switch>
      {routes
        .filter(({ Component }) => !!Component)
        .map(({ path, key, Component, private: isPrivate, exact, ...rest }, index) => {
          return (
            <Route
              exact={exact}
              path={path}
              key={`route-${key}-${index}`}
              render={() => {
                if (!isPrivate || (isPrivate && authed && !guest)) {
                  return (
                    <Suspense fallback={renderFallback()}>
                      <CustomContentRenderer private={!!isPrivate} routeKey={key} path={path} Component={Component} {...rest} />
                    </Suspense>
                  );
                }
                return (
                  <Redirect
                    to={{
                      pathname: linkHandler.get('login'),
                      state: { from: { pathname: path, key } }
                    }}
                  />
                );
              }}
            />
          );
        })}
      {redirects.reduce(
        (acc, { redirects, path, key }, index) =>
          acc.concat(redirects.map((from, rx) => <Redirect exact from={from} to={path} key={`redirect-entry-${key}-${index}-${rx}`} />)),
        []
      )}
      {children}
      {REDIRECTS.map(({ from, to }, index) => (
        <Redirect key={`redirect-${index}`} from={from} to={to} />
      ))}

      <Route path="/*" component={NotFoundPage} />
    </Switch>
  );
};
